import React, { useEffect } from 'react';
import { Button, ProposalCard, useWindowDimensions } from '@insurancemenu/imenu-components-lib';
import { FileSignature, Mail, Person } from '@insurancemenu/imenu-components-lib/dist/icons';
import ProductLineTitle from 'components/common/general/ProductLineTitle';
import { useQueryParams } from 'global/hooks/useQuery';
import { useRootStore } from 'store/rootStore';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { toJS } from 'mobx';
import { CartPageType } from 'store/ui-stores/cart/proposalCartStore';
import { observer } from 'mobx-react-lite';
import { normDateToShortAmericanDate } from '@insurancemenu/imenu-components-lib/dist/helpers/dateFormatters';
import {
  PRODUCT_LINES_CONTENT_MAP,
  sortByProductLineId,
} from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { useHistory } from 'react-router-dom';
import clsx from 'clsx';
import FormsSpinner from 'components/common/general/FormSpinner';
import { breakpointsValues } from '@insurancemenu/imenu-components-lib/dist/utils';
import CommonHeader from 'components/common/headers/CommonHeader';
import { CartItem } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import ProductLineCard from 'components/common/general/ProductLineCard';
import { INITIAL_FILTER } from 'store/ui-stores/browsing/browsingFilterStore';
import ChoiceModal from 'components/common/modals/ChoiceModal';
import ActionModal from 'components/common/modals/ActionModal';
import { ModalType } from 'global/utils/ModalsHelper';
import CarrierRulesViolation from 'components/common/general/CarrierRulesViolation';
import InfoSection from '../InfoSection';
import { createStyles } from './styles';
import CartItemSelectBar from './CartItemSelectBar';

export type HeaderRedirectsMap = {
  [key in CartPageType]: () => void;
};

const CartContent = observer((): JSX.Element => {
  const history = useHistory();

  const query = useQueryParams();
  const cartId = query.get('cartId') || null;
  const groupId = query.get('groupId') || '';
  const type = query.get('cartType') || null;

  // Show different layout for mobile and tablet view
  const { width: windowWidth } = useWindowDimensions();
  const isMobileView = windowWidth <= breakpointsValues.lg;

  // Mobx store
  const {
    domainStore: { domainHeader, cartItemsQuantity, openChoiceModal },
    proposalCartStore: {
      loading,
      portfolios,
      marketingTags,
      productLineAttrs,
      cart,
      loaderInPortfolioId,
      showCrossSellModal,
      checkUnusedProductLineIds,
      checkCarrierProductLinesRules,
      setBrowsingCriteria,
      onViewPlanDetails,
      collectCartData,
      setShowCrossSellModal,
      resetInitialData,
    },
    helpMeDecideStore: { setCriteriaIndex, currentProductLine: helpMeDecidePLine },
    popularPlansStore: { currentProductLineId: popularPLine },
    browsingFilterStore: { setFilters },
  } = useRootStore();

  const convertedCart = toJS(cart);
  const convertedDomain = toJS(domainHeader);

  const convertedPortfolios = portfolios.map((dateObj) => toJS(dateObj)).slice();
  // Used Product line ids, that are added to cart
  const productLineIds = [...new Set(convertedCart.cartItems.map((item) => item.productLineId))].sort(
    sortByProductLineId,
  );
  const convertedMarketingTags = marketingTags.map((dateObj) => toJS(dateObj)).slice();
  const convertedProductLineAttrs = productLineAttrs.map((attr) => toJS(attr)).slice();
  const invalidProductLineIdsWithMessage = checkCarrierProductLinesRules();
  const unusedProductLineIds = checkUnusedProductLineIds();

  const { effectiveDate, zipCode } = convertedCart.lastUpdateBrowsingCriteria;

  const currentGroupId = groupId || convertedCart.groupId;
  const currentPLineId = productLineIds[0] || unusedProductLineIds[0];

  const classes = createStyles(convertedCart.cartItems.length === 0);

  useEffect(() => {
    if (cartId) void collectCartData(cartId);
    return resetInitialData;
  }, []);

  const getCartItemsByProductLine = (productLineId: ProductLineId): CartItem[] => {
    const result = convertedCart.cartItems.filter((item) => item.productLineId === productLineId);
    if (result) {
      return result;
    }
    throw new Error(`Cannot find items for this product line id: ${productLineId}`);
  };

  const onBrowsingChange = (id: string, selectedBcIndex: number): void => {
    setBrowsingCriteria(id, selectedBcIndex);
  };

  const onProductLineClick = (e: React.SyntheticEvent<HTMLLIElement>): void => {
    const selectedProductLineId = e.currentTarget.dataset.productLine;
    history.push(
      `/popular-plans?cartId=${cartId}&productLineId=${selectedProductLineId}&groupId=${currentGroupId}&cartType=popular`,
    );
  };

  const proposalPanelInfo = {
    'Effective Date': normDateToShortAmericanDate(effectiveDate),
    'Zip Code': zipCode,
  };

  // Add back button redirects here for all pages that have cart link
  const headerRedirects: HeaderRedirectsMap = Object.freeze({
    'rate-segment': () => {
      history.push(`/rate-segments?cartId=${cartId}&groupId=${currentGroupId}&cartType=rate-segment`);
    },
    popular: () => {
      history.push(
        `/popular-plans?cartId=${cartId}&productLineId=${
          popularPLine || currentPLineId
        }&groupId=${currentGroupId}&cartType=popular`,
      );
    },
    browsing: () => {
      history.push(`/browsing?groupId=${currentGroupId}&cartId=${cartId}&cartType=browsing`);
    },
    'help-me-decide': () => {
      // Set first criteria for help me decide flow and reset filters
      setCriteriaIndex(1);
      setFilters(INITIAL_FILTER);
      if (helpMeDecidePLine) {
        history.push(
          `/help-me-decide?groupId=${groupId}&productLineId=${helpMeDecidePLine}&cartId=${cartId}&cartType=help-me-decide`,
        );
      } else {
        history.push(
          `/popular-plans?cartId=${cartId}&productLineId=${
            popularPLine || currentPLineId
          }&groupId=${currentGroupId}&cartType=popular`,
        );
      }
    },
  });

  const actionsSection = (
    <>
      {convertedDomain.properties?.eStoreEnrollAvailable && (
        <Button
          type="text"
          color="primary"
          leftIconProp={<FileSignature />}
          className={classes.actionButton}
          disabled={cartItemsQuantity === 0 || invalidProductLineIdsWithMessage.length > 0}
          onClick={(): void => {
            openChoiceModal('ENROLL_NOW');
          }}
        >
          Enroll now
        </Button>
      )}
      <Button
        type="text"
        color="primary"
        leftIconProp={<Mail />}
        className={classes.actionButton}
        disabled={cartItemsQuantity === 0}
        onClick={(): void => {
          openChoiceModal('SAVE_CART');
        }}
      >
        Save cart and email it
      </Button>
      <Button
        type="text"
        color="primary"
        leftIconProp={<Person />}
        className={classes.actionButton}
        disabled={cartItemsQuantity > 0 && invalidProductLineIdsWithMessage.length > 0}
        onClick={(): void => {
          openChoiceModal('CONTACT_ME');
        }}
      >
        {windowWidth <= 540 ? 'Contact me' : 'Have team member contact me'}
      </Button>
    </>
  );

  const BenefitsCards = unusedProductLineIds.map((pLineId) => (
    <ProductLineCard
      key={pLineId}
      pLineId={pLineId}
      onCardClick={onProductLineClick}
      icon={PRODUCT_LINES_CONTENT_MAP[pLineId].icon('4rem')}
      label={PRODUCT_LINES_CONTENT_MAP[pLineId].label}
    />
  ));

  return (
    <div className={classes.innerContent}>
      {loading ? (
        <FormsSpinner display={loading} semiTransparentBg={false} />
      ) : (
        <div>
          <CommonHeader
            title="My Cart"
            showBackButton={type !== 'quote'}
            headerClassName={classes.header}
            onRedirect={(type && headerRedirects[type as CartPageType]) || undefined}
          />
          <InfoSection mainInfo={proposalPanelInfo} actionsSection={actionsSection} />
          <div className={classes.productLineSectionsWrapper}>
            {productLineIds &&
              productLineIds.map((productLineId) => (
                // ProductLine section
                <div className={classes.section} key={productLineId}>
                  <ProductLineTitle productLineId={productLineId} styles={classes.productLineTitle} />
                  <div className={classes.cardsWrapper}>
                    {getCartItemsByProductLine(productLineId).map((cartItem) => {
                      const portfolio = convertedPortfolios.find(
                        (portfolioItem) => portfolioItem.id === cartItem.portfolioId,
                      );
                      if (portfolio) {
                        return (
                          <div className={classes.cartItem} key={portfolio.id}>
                            <CartItemSelectBar portfolio={portfolio} isMobileView={isMobileView} />
                            <ProposalCard
                              productLineAttributes={convertedProductLineAttrs}
                              marketingTags={convertedMarketingTags}
                              portfolio={portfolio}
                              cartItem={cartItem}
                              isPlanConfigurable={false}
                              isProposalConfirmed
                              userRoles={[]}
                              onViewPlanDetails={(portfolioId: string): void => {
                                onViewPlanDetails(portfolioId, type || '', history.push);
                              }}
                              onBrowsingChange={onBrowsingChange}
                              breakpoint={breakpointsValues.md}
                              loading={portfolio.id === loaderInPortfolioId}
                            />
                          </div>
                        );
                      }
                      return <></>;
                    })}
                  </div>
                </div>
              ))}
          </div>
          {/* Additional benefits */}
          {unusedProductLineIds.length > 0 && (
            <section className={clsx(classes.section, classes.benefitsSection)}>
              <h3 className={classes.benefitsTitle}>Check out other product lines:</h3>
              <ul className={classes.benefitsList}>{BenefitsCards}</ul>
            </section>
          )}
          {invalidProductLineIdsWithMessage.length > 0 && (
            <section className={clsx(classes.section, classes.benefitsSection)}>
              <CarrierRulesViolation invalidProductLineIdsWithMessage={invalidProductLineIdsWithMessage} />
            </section>
          )}

          {/* Buttons Wrapper */}
          <div className={classes.actionButtons}>
            <Button
              color="primary"
              size="large"
              disabled={cartItemsQuantity === 0 || invalidProductLineIdsWithMessage.length > 0}
              onClick={(): void => {
                openChoiceModal('ALL');
              }}
            >
              Proceed
            </Button>
          </div>
          <ChoiceModal />
          <ActionModal
            show={showCrossSellModal}
            title="Interested in seeing other product lines?"
            subtitle="You can add anything you like to your current cart"
            closeButtonText="Close"
            modalContent={
              <>
                <ul className={clsx(classes.benefitsList, classes.crossSellModalProductLinesList)}>{BenefitsCards}</ul>
                <CarrierRulesViolation invalidProductLineIdsWithMessage={invalidProductLineIdsWithMessage} />
              </>
            }
            onClose={(): void => setShowCrossSellModal(false)}
            loading={loading}
            closable={false}
            modalType={ModalType.NO_ICON}
          />
        </div>
      )}
    </div>
  );
});

export default CartContent;
